/**
 * Backend Entity Adapters
 * 
 * This file contains entity adapter factories that connect to real backend APIs.
 * These adapters use dependency injection to receive the API client with authentication.
 */

import { EntityAdapterFactory, ApiClient, SearchResult } from "./entityRegistry";
import { translatePosition } from "@/lib/utils/player-utils";

/**
 * Team entity adapter factory for backend API
 * Integrates with the Teams API v1.0
 */
export const createTeamAdapter: EntityAdapterFactory = (apiClient: ApiClient) => ({
  findById: async (id: string) => {
    try {
      const team = await apiClient.apiRequest<{
        _id: string;
        title: string;
        displayName?: string;
        logoUrl?: string;
        thumbnailUrl?: string;
      }>(`/teams/${id}`);
      
      if (!team) return null;
      return { id: team._id, label: team.displayName || team.title, thumbnailUrl: team.thumbnailUrl };
    } catch (error) {
      console.error('Failed to fetch team:', error);
      return null;
    }
  },

  search: async (q: string, limit = 10) => {
    try {
      // Empty → alphabetical defaults, no textSearch param
      if (!q) {
        const response = await apiClient.apiRequest<{
          docs: Array<{ _id: string; title: string; displayName?: string; logoUrl?: string; thumbnailUrl?: string }>;
        }>(`/teams?limit=${Math.min(limit, 20)}&page=1`);
        return (response.docs || [])
          .sort((a, b) => a.title.localeCompare(b.title, 'de'))
          .map((team): SearchResult => ({ id: team._id, label: team.displayName || team.title, thumbnailUrl: team.thumbnailUrl }));
      }

      // ≥1 char → text search
      const response = await apiClient.apiRequest<{
        docs: Array<{ _id: string; title: string; displayName?: string; logoUrl?: string; thumbnailUrl?: string; createdAt: string; updatedAt: string }>;
      }>(`/teams?textSearch=${encodeURIComponent(q)}&limit=${Math.min(limit, 20)}&page=1`);

      return response.docs?.map((team): SearchResult => ({
        id: team._id,
        label: team.displayName || team.title,
        thumbnailUrl: team.thumbnailUrl,
      })) || [];
    } catch (error) {
      console.error('Failed to search teams:', error);
      return [];
    }
  }
});

/**
 * Player entity adapter factory for backend API
 * Integrates with the Players API v1.0
 */
export const createPlayerAdapter: EntityAdapterFactory = (apiClient: ApiClient) => ({
  findById: async (id: string) => {
    try {
      const player = await apiClient.apiRequest<{
        _id: string;
        firstName: string;
        lastName: string;
        nickname: string;
        position: string;
        jerseyNumber: number;
        birthDate: string;
        birthDateConsent: boolean;
        nationality: string;
        height: number;
        weight: number;
        biography: string;
        belongsToTeamId: string;
        belongsToSecondaryTeamIds: string[];
        createdAt: string;
        updatedAt: string;
        imageUrl: string;
        thumbnailUrl?: string;
        imageStates?: Array<{
          _id: string;
          state: {
            active?: {
              url?: string;
              thumbnailUrl?: string;
            };
          };
        }>;
        selectableEntities: {
          teams: Array<{ _id: string; value: string }>;
        };
      }>(`/players/${id}`);
      
      if (!player) return null;
      
      const teamTitle = player.selectableEntities?.teams?.find(
        (team) => team._id === player.belongsToTeamId
      )?.value || '';
      
      return {
        id: player._id,
        firstName: player.firstName,
        lastName: player.lastName,
        fullName: `${player.firstName} ${player.lastName}`,
        nickname: player.nickname,
        position: player.position,
        jerseyNumber: player.jerseyNumber,
        birthDate: player.birthDate,
        nationality: player.nationality,
        height: player.height,
        weight: player.weight,
        biography: player.biography,
        imageUrl: player.imageUrl,
        thumbnailUrl: player.thumbnailUrl,
        imageStates: player.imageStates,
        teamTitle
      };
    } catch (error) {
      console.error('Failed to fetch player:', error);
      return null;
    }
  },

  search: async (q: string, limit = 10, teamId?: string) => {
    try {
      // Empty → alphabetical defaults, no textSearch param
      if (!q) {
        const params = new URLSearchParams({ limit: Math.min(limit, 20).toString(), page: '1' });
        if (teamId) params.append('teamId', teamId);

        const response = await apiClient.apiRequest<{
          players: { docs: Array<{ _id: string; firstName: string; lastName: string; position: string | null; jerseyNumber: number | null; belongsToTeamId?: string; thumbnailUrl?: string }> };
          listEntities?: { teams?: Array<{ _id: string; value: string }> };
        }>(`/players?${params.toString()}`);

        return (response.players?.docs || [])
          .sort((a, b) => a.lastName.localeCompare(b.lastName, 'de'))
          .slice(0, limit)
          .map((player): SearchResult => {
            const team = response.listEntities?.teams?.find(t => t._id === player.belongsToTeamId);
            const positionAndNumber = `${translatePosition(player.position ?? undefined)}${player.jerseyNumber !== null && player.jerseyNumber !== undefined ? ` #${player.jerseyNumber}` : ''}`;
            const subTitle = [positionAndNumber, team?.value].filter(Boolean).join(' • ');
            
            return {
              id: player._id,
              label: `${player.firstName} ${player.lastName}`,
              subTitle: subTitle || undefined,
              thumbnailUrl: player.thumbnailUrl,
            };
          });
      }

      // ≥1 char → text search
      const params = new URLSearchParams({ textSearch: q, limit: Math.min(limit, 20).toString(), page: '1' });
      if (teamId) params.append('teamId', teamId);

      const response = await apiClient.apiRequest<{
        players: { docs: Array<{ _id: string; firstName: string; lastName: string; position: string | null; jerseyNumber: number | null; belongsToTeamId?: string; thumbnailUrl?: string }> };
        listEntities?: { teams?: Array<{ _id: string; value: string }> };
      }>(`/players?${params.toString()}`);

      return response.players?.docs?.map((player): SearchResult => {
        const team = response.listEntities?.teams?.find(t => t._id === player.belongsToTeamId);
        const positionAndNumber = `${translatePosition(player.position ?? undefined)}${player.jerseyNumber !== null && player.jerseyNumber !== undefined ? ` #${player.jerseyNumber}` : ''}`;
        const subTitle = [positionAndNumber, team?.value].filter(Boolean).join(' • ');

        return {
          id: player._id,
          label: `${player.firstName} ${player.lastName}`,
          subTitle: subTitle || undefined,
          thumbnailUrl: player.thumbnailUrl,
        };
      }) || [];
    } catch (error) {
      console.error('Failed to search players:', error);
      return [];
    }
  }
});

/**
 * Match entity adapter factory for backend API
 * Integrates with the Matches API v1.0
 */
export const createMatchAdapter: EntityAdapterFactory = (apiClient: ApiClient) => ({
  findById: async (id: string) => {
    try {
      const match = await apiClient.apiRequest<{
        _id: string;
        date: string;
        location: string;
        leagueName: string;
        ourScore: number;
        opponentScore: number;
        isWin: boolean | null;
        homeOrAway: 'HOME' | 'AWAY';
        team: { name: string; logoUrl?: string };
        belongsToOpponentId?: string;
        opponent?: { name: string; logoUrl?: string };
        createdAt: string;
        updatedAt: string;
      }>(`/matches/${id}`);
      
      if (!match) return null;

      const ourName = match.team?.name || '';
      const ourLogo = match.team?.logoUrl || '';
      
      let opponentName = match.opponent?.name || '';
      let opponentLogo = match.opponent?.logoUrl || '';

      if (!opponentName && match.belongsToOpponentId) {
        try {
          const opponent = await apiClient.apiRequest<{ name: string; logoUrl: string }>(
            `/opponents/${match.belongsToOpponentId}`, 'GET', undefined, undefined,
            { skipGlobalErrorHandler: true, silent: true, throwOnError: true }
          );
          if (opponent) {
            opponentName = opponent.name;
            opponentLogo = opponent.logoUrl;
          }
        } catch (e) {
          // Opponent might be deleted, ignore error
        }
      }

      let homeName = '';
      let homeLogo = '';
      let homeScore = 0;
      let awayName = '';
      let awayLogo = '';
      let awayScore = 0;

      if (match.homeOrAway === 'HOME') {
        homeName = ourName;
        homeLogo = ourLogo;
        homeScore = match.ourScore || 0;
        awayName = opponentName;
        awayLogo = opponentLogo;
        awayScore = match.opponentScore || 0;
      } else {
        // If AWAY, opponent is Home
        homeName = opponentName;
        homeLogo = opponentLogo;
        homeScore = match.opponentScore || 0;
        awayName = ourName;
        awayLogo = ourLogo;
        awayScore = match.ourScore || 0;
      }
      
      return {
        id: match._id,
        opponentName,
        date: match.date,
        location: match.location,
        leagueName: match.leagueName,
        ourScore: match.ourScore,
        opponentScore: match.opponentScore,
        isWin: match.isWin,
        teamTitle: ourName,
        homeOrAway: match.homeOrAway === 'HOME' ? 'Heimspiel' : 'Auswärtsspiel',
        homeScore, homeName, homeLogo,
        awayScore, awayName, awayLogo,
        ourLogo, opponentLogo,
        createdAt: match.createdAt,
        updatedAt: match.updatedAt
      };
    } catch (error) {
      console.error('Failed to fetch match:', error);
      return null;
    }
  },

  search: async (q: string, limit = 10, teamId?: string) => {
    try {
      type MatchListResponse = {
        matches: { docs: Array<{ _id: string; date: string; belongsToOpponentId: string; belongsToTeamId: string; ourScore?: number | null; opponentScore?: number | null }> };
        listEntities: { teams: Array<{ _id: string; value: string }>; opponents: Array<{ _id: string; value: string }> };
      };

      const mapDocs = (response: MatchListResponse): SearchResult[] =>
        (response.matches?.docs || []).map((match): SearchResult => {
          const teamTitle = response.listEntities?.teams?.find(t => t._id === match.belongsToTeamId)?.value || '';
          const opponentName = response.listEntities?.opponents?.find(o => o._id === match.belongsToOpponentId)?.value || 'Unbekannt';
          
          const dateObj = new Date(match.date);
          const dateStr = dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
          const timeStr = dateObj.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
          
          let scoreStr = '';
          if (match.ourScore != null && match.opponentScore != null) {
            scoreStr = ` | ${match.ourScore}:${match.opponentScore}`;
          }

          return {
            id: match._id,
            label: `${teamTitle} vs ${opponentName}`,
            subTitle: `${dateStr} ${timeStr} Uhr${scoreStr}`
          };
        });

        // Fetch a bit more to sort and filter them properly
        const params = new URLSearchParams({ limit: '20', page: '1', filterType: 'relative'});

        if(q) {
          params.append("textSearch", q)
        }

        if (teamId) params.append('teamId', teamId);
        const response = await apiClient.apiRequest<MatchListResponse>(`/matches?${params.toString()}`);
        
        const now = new Date().getTime();
        
        // Sort by proximity to current date, with a bias towards future matches
        const sortedDocs = (response.matches?.docs || []).sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          
          const diffA = dateA - now;
          const diffB = dateB - now;
          
          // Apply a weight to make past matches appear "further away" 
          // (e.g. multiplying past difference by 1.5 makes it 50% less likely to win over a future match with same absolute distance)
          const weightA = diffA < 0 ? Math.abs(diffA) * 1.5 : diffA;
          const weightB = diffB < 0 ? Math.abs(diffB) * 1.5 : diffB;
          
          return weightA - weightB;
        });
        
        // Temporarily assign sorted docs back to response to use the mapDocs function
        const sortedResponse = { ...response, matches: { docs: sortedDocs } };
        
        // Only return 5 items as requested
        return mapDocs(sortedResponse).slice(0, 5);
    } catch (error) {
      console.error('Failed to search matches:', error);
      return [];
    }
  },
});

/**
 * Sponsor entity adapter factory for backend API
 * Integrates with the Sponsors API v1.0
 */
export const createSponsorAdapter: EntityAdapterFactory = (apiClient: ApiClient) => ({
  findById: async (id: string) => {
    try {
      const sponsor = await apiClient.apiRequest<{
        _id: string;
        name: string;
        address: string | null;
        website: string;
        mandantType: string;
        department: string;
        type: string;
        contractStartDate: string;
        contractEndDate: string;
        status: string;
        notes: string;
        volume: number;
        currency: string;
        street: string | null;
        streetNumber: string | null;
        zip: string | null;
        city: string | null;
        country: string | null;
        mobile: string | null;
        phone: string | null;
        isPostable: boolean;
        createdAt: string;
        updatedAt: string;
        logoUrl: string;
        thumbnailUrl?: string;
        selectableEntities: { users: Array<{ _id: string; value: string }> };
      }>(`/sponsors/${id}`);
      
      if (!sponsor) return null;
      
      return {
        id: sponsor._id,
        name: sponsor.name,
        website: sponsor.website,
        type: sponsor.type,
        status: sponsor.status,
        volume: sponsor.volume,
        currency: sponsor.currency,
        logoUrl: sponsor.logoUrl,
        thumbnailUrl: sponsor.thumbnailUrl,
        isPostable: sponsor.isPostable,
        createdAt: sponsor.createdAt,
        updatedAt: sponsor.updatedAt
      };
    } catch (error) {
      console.error('Failed to fetch sponsor:', error);
      return null;
    }
  },

  search: async (q: string, limit = 10) => {
    try {
      type SponsorDoc = {
        _id: string; name: string; createdAt: string; updatedAt: string;
        isPostable: boolean; hasActiveAgreement: boolean; hasSocialMediaAgreement: boolean;
        thumbnailUrl?: string;
      };

      const sortAndMap = (docs: SponsorDoc[]): SearchResult[] => {
        const sorted = [...docs].sort((a, b) => {
          if (a.hasSocialMediaAgreement !== b.hasSocialMediaAgreement) return a.hasSocialMediaAgreement ? -1 : 1;
          if (a.hasActiveAgreement !== b.hasActiveAgreement) return a.hasActiveAgreement ? -1 : 1;
          return a.name.localeCompare(b.name, 'de');
        });
        return sorted.map((sponsor): SearchResult => {
          let subTitle: string | undefined;
          if (!sponsor.hasActiveAgreement) subTitle = '⚠ Keine aktive Vereinbarung';
          else if (!sponsor.hasSocialMediaAgreement) subTitle = '⚠ Keine Social-Media-Vereinbarung';
          return { id: sponsor._id, label: sponsor.name, subTitle, thumbnailUrl: sponsor.thumbnailUrl };
        });
      };

      // Empty → priority defaults, no textSearch param
      if (!q) {
        const response = await apiClient.apiRequest<{ docs: SponsorDoc[] }>(
          `/sponsors?limit=${Math.min(limit, 20)}&page=1`
        );
        return sortAndMap(response.docs || []).slice(0, limit);
      }

      // ≥1 char → text search
      const response = await apiClient.apiRequest<{
        limit: number; offset: number; page: number;
        hasNextPage: boolean; hasPrevPage: boolean; count: number; pageCount: number;
        docs: SponsorDoc[];
      }>(`/sponsors?textSearch=${encodeURIComponent(q)}&limit=${Math.min(limit, 20)}&page=1`);

      return sortAndMap(response.docs || []);
    } catch (error) {
      console.error('Failed to search sponsors:', error);
      return [];
    }
  }
});
